import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import type { ISODate } from '@app/shared'

import { data, useCaptureQuick, useClipPreview, useClock, type CaptureType } from '../../lib/data'
import { CAPTURE_TYPES, captureTypeLabel, detect } from '../../components/capture/detect'
import {
  clearDraft,
  draftType,
  hasContent,
  withText,
  withType,
  type CaptureDraft,
} from '../lib/captureDraft'
import { MArrowRight, MMic } from '../shell/icons'
import { Eyebrow } from '../shell/parts'
import { IN_ONE_HOUR_LABEL, TIME_PRESETS, inOneHour } from '../../components/tasks/presets'
import { PhoneMayMissNote } from '../../components/tasks/PhoneMayMissNote'

// ============================================================================
// 1c · THE CAPTURE SHEET — docs/design/handoff-mobile/README.md §1c.
//
// "Capture anything from anywhere, with nothing to fill in." An OVERLAY, NOT A
// ROUTE: the screen behind keeps its place under `rgba(16,24,29,.5)` +
// `backdrop-filter:blur(1.5px)`, and closing puts you back exactly where you
// were — mid-scroll, mid-sentence, on whichever tab you were on.
//
// THE INPUT IS UNCONTROLLED, which is the same undo contract the journal
// composer carries and the same one no gate can photograph. Detection re-renders
// this component on every keystroke; if React owned the field's value, that
// re-render would rewrite it and the browser's native undo stack would be gone
// after the first character. Nothing is ever written back into the field except
// on an explicit reset (a successful save, or a share arriving).
//
// DETECTION IS THE EXISTING ONE — components/capture/detect.ts, the three
// ordered rules the ⌘K palette already runs (URL → clip, date phrase → reminder,
// imperative → task). There is deliberately no second, phone-only parser: two
// parsers would mean the same sentence filing two different ways depending on
// which mouth it went in, and the ABSENCE of a chip is the honest feedback for
// everything the rules do not recognise ("optional — unset goes to Inbox").
// ============================================================================

export function CaptureSheet({
  open,
  draft,
  onDraft,
  onClose,
  onRecord,
}: {
  open: boolean
  draft: CaptureDraft
  onDraft: (next: CaptureDraft) => void
  onClose: () => void
  /** The mic button in the sheet. Same recorder the hold gesture opens. */
  onRecord: () => void
}) {
  const field = useRef<HTMLTextAreaElement>(null)
  const navigate = useNavigate()
  const { data: clock } = useClock()
  const capture = useCaptureQuick()

  const [saved, setSaved] = useState<{ destination: string; href: string } | null>(null)
  /**
   * THE REMINDER'S CLOCK, when a preset chip was tapped (owner, 2026-09-14: "presets … 1 hour
   * from now, 11 am, 3 pm, 10 pm … should apply to both the calendar and the reminders page").
   * Null means the long-standing default — 9:00 on the day. Held here, not in the persisted
   * draft: a time picked for one thought must not attach itself to the next.
   */
  const [remindAt, setRemindAt] = useState<{ label: string; date: ISODate; hhmm: string } | null>(null)
  /** How far down the sheet has been dragged, in px. Swipe-to-dismiss. */
  const [dragY, setDragY] = useState(0)
  const dragFrom = useRef<number | null>(null)

  const detection = detect(draft.text, clock?.date)
  const activeType = draftType(draft, detection)
  const preview = useClipPreview(detection.type === 'clip' ? draft.text.trim() : undefined)

  // THE FIELD IS RESTORED, NOT RESET. A sheet that opens empty over a half-typed
  // thought is the drafting promise broken; the value is pushed into the
  // uncontrolled node exactly once per open, and never again while it is open.
  useEffect(() => {
    if (!open) return
    setSaved(null)
    setDragY(0)
    setRemindAt(null)
    capture.reset()
    const node = field.current
    if (node) {
      node.value = draft.text
      node.focus()
      node.setSelectionRange(node.value.length, node.value.length)
    }
    // `draft.text` is deliberately NOT a dependency: it changes on every
    // keystroke, and re-running this would move the caret to the end mid-word.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  function save(andOpen: boolean) {
    const value = field.current?.value.trim() ?? ''
    if (!value) return
    // A PRESET DATES THE ROW TOO. The clock presets land on the detected day when the words
    // named one, else today; "1 hour from now" carries its own day. Only a Reminder sends it.
    const reminder =
      activeType === 'reminder' && remindAt
        ? { due: remindAt.date, reminderAt: new Date(`${remindAt.date}T${remindAt.hhmm}`).toISOString() }
        : { due: detection.due as ISODate | null }
    capture.mutate(
      { text: value, type: activeType, ...reminder },
      {
        onSuccess: (result) => {
          setSaved({ destination: result.destination, href: result.href })
          onDraft(clearDraft())
          if (field.current) field.current.value = ''
          if (andOpen) {
            onClose()
            navigate(result.href)
          } else {
            // README §1c: "`Save` closes and returns to the untouched screen
            // behind." It closes on the NEXT tick so the confirmation is read
            // by a screen reader before the dialog goes.
            setTimeout(onClose, 0)
          }
        },
      },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Capture anything"
    >
      {/* THE SCRIM. A button, so a tap outside closes and so the escape hatch is
          reachable by keyboard for anyone who tabs into it. The blur is on the
          scrim rather than on the app, because the mobile shell has no wrapper
          to filter without also filtering this sheet. */}
      <button
        type="button"
        aria-label="Close capture"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-scrim backdrop-blur-[1.5px] motion-safe:animate-[fade-in_var(--dur-scrim)_var(--ease-out)]"
      />

      <div
        className="relative flex max-h-full flex-col overflow-hidden rounded-t-pill bg-surface shadow-sheet"
        style={{
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
          transition: dragFrom.current === null ? 'transform var(--dur-sheet) var(--ease-out)' : undefined,
        }}
      >
        {/* THE DRAG HANDLE, and it drags. README §Interactions: "Swipe down or
            scrim tap closes and discards nothing already typed." Past a third of
            the sheet the release closes; anything less springs back. */}
        <div
          className="flex flex-none touch-none justify-center pb-1 pt-[10px]"
          onPointerDown={(e) => {
            dragFrom.current = e.clientY
            e.currentTarget.setPointerCapture(e.pointerId)
          }}
          onPointerMove={(e) => {
            if (dragFrom.current === null) return
            setDragY(Math.max(0, e.clientY - dragFrom.current))
          }}
          onPointerUp={(e) => {
            const height = e.currentTarget.parentElement?.offsetHeight ?? 0
            dragFrom.current = null
            if (dragY > height / 3) onClose()
            else setDragY(0)
          }}
          onPointerCancel={() => {
            dragFrom.current = null
            setDragY(0)
          }}
        >
          <span className="h-1 w-[38px] rounded-[2px] bg-line-2" aria-hidden />
        </div>

        <div className="flex min-h-0 flex-col gap-[14px] overflow-y-auto px-[18px] pt-3">
          {/* 1 · THE INPUT ROW. No label, no placeholder ceremony. */}
          <div className="flex items-start gap-3">
            <label className="sr-only" htmlFor="mobile-capture-field">
              Capture anything — no required fields
            </label>
            <textarea
              id="mobile-capture-field"
              ref={field}
              rows={2}
              autoComplete="off"
              placeholder="Capture anything…"
              onChange={(e) => onDraft(withText(draft, e.currentTarget.value))}
              // No `.mo-field` wrapper here — the row also holds the mic
              // button, and the wrapper's `:focus-within` ring would light up
              // the whole row on a tap that has nothing to do with the field.
              // `outline-none` is dropped instead, so the global
              // `*:focus-visible` ring (index.css) lands on the field itself.
              className="min-h-[56px] min-w-0 flex-1 resize-none border-0 bg-transparent p-0 text-[19px] font-normal leading-[1.4] text-ink placeholder:text-microcopy text-pretty"
            />
            <button
              type="button"
              onClick={onRecord}
              aria-label="Record a voice memo instead"
              className="flex size-11 flex-none items-center justify-center rounded-full bg-fill-2 text-ink-2 active:bg-fill"
            >
              <MMic size={20} strokeWidth={1.9} />
            </button>
          </div>

          {/* 2 · THE DETECTED-TYPE PREVIEW. "Detection is shown, never asked." */}
          {detection.type === 'clip' && preview.data && (
            <div className="flex flex-col gap-2 rounded-preview border border-orange-line bg-orange-fill px-[14px] py-[13px]">
              <div className="flex items-center gap-2">
                <span className="rounded-pill bg-orange-pill px-2 py-[3px] text-[9.5px] font-semibold leading-[1.4] tracking-[0.06em] text-orange">
                  CLIP
                </span>
                <span className="text-[11px] font-medium leading-none text-orange">
                  link detected
                </span>
              </div>
              {/* Null until something has actually fetched it. A title invented
                  from a URL slug is exactly the fabricated knowledge the product
                  refuses, so the URL itself stands in and says so below. */}
              <p className="text-[14.5px] font-semibold leading-[1.35] text-ink text-pretty">
                {preview.data.title ?? preview.data.url}
              </p>
              <p className="text-[11.5px] font-normal leading-[1.45] text-muted">
                {preview.data.sourceDetail} ·{' '}
                {preview.data.title === null
                  ? 'no title fetched yet'
                  : 'fetched just now'}{' '}
                · will file under {preview.data.filesUnder}
              </p>
            </div>
          )}

          {/* The other two detections have no preview card, so they say what they
              matched in one quiet line instead — the same promise, one step down. */}
          {detection.type !== null && detection.type !== 'clip' && !draft.touched && (
            <p className="text-[11px] leading-[1.4] text-microcopy">
              {captureTypeLabel(detection.type)} detected — {detection.because}
              {detection.due && ` · ${detection.due}`}. Change it below, or leave it.
            </p>
          )}

          {/* 3 · THE TYPE CHIPS. */}
          <div className="flex flex-col gap-2">
            <Eyebrow className="text-[9.5px] tracking-[0.08em] text-microcopy">TYPE</Eyebrow>
            <div className="flex flex-wrap gap-2">
              {ORDERED_TYPES(activeType).map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={activeType === t}
                  onClick={() => onDraft(withType(draft, activeType, t))}
                  className={clsx(
                    'flex h-[38px] items-center rounded-pill px-[15px] text-[13px] leading-none',
                    'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                    activeType === t
                      ? 'bg-ink font-semibold text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]'
                      : 'bg-fill-2 font-medium text-ink-2 active:bg-fill',
                  )}
                >
                  {captureTypeLabel(t)}
                </button>
              ))}
            </div>
            <p className="text-[11px] font-normal leading-[1.4] text-microcopy">
              optional — unset goes to Inbox
            </p>
          </div>

          {/* 3b · WHEN — only for a Reminder. The same four presets as the desktop Reminders
              composers (components/tasks/presets.ts). Nothing here is required: with no chip
              tapped the reminder keeps its default, and the line beneath says which clock that is
              so the default is never a surprise. */}
          {activeType === 'reminder' && (
            <div className="flex flex-col gap-2">
              <Eyebrow className="text-[9.5px] tracking-[0.08em] text-microcopy">WHEN</Eyebrow>
              <div className="flex flex-wrap gap-2">
                <WhenChip
                  active={remindAt?.label === IN_ONE_HOUR_LABEL}
                  onClick={() => {
                    const at = inOneHour()
                    setRemindAt(
                      remindAt?.label === IN_ONE_HOUR_LABEL ? null : { label: IN_ONE_HOUR_LABEL, ...at },
                    )
                  }}
                >
                  {IN_ONE_HOUR_LABEL}
                </WhenChip>
                {TIME_PRESETS.map((p) => (
                  <WhenChip
                    key={p.hhmm}
                    active={remindAt?.label === p.label}
                    onClick={() =>
                      setRemindAt(
                        remindAt?.label === p.label
                          ? null
                          : {
                              label: p.label,
                              date: (detection.due as ISODate | null) ?? clock?.date ?? localToday(),
                              hhmm: p.hhmm,
                            },
                      )
                    }
                  >
                    {p.label}
                  </WhenChip>
                ))}
              </div>
              <p className="text-[11px] font-normal leading-[1.4] text-microcopy">
                {remindAt
                  ? `reminds ${remindAt.date === (clock?.date ?? localToday()) ? 'today' : remindAt.date} at ${clockLabel(remindAt.hhmm)} — tap again to clear`
                  : `optional — unset reminds at 9:00 AM ${detection.due ? `on ${detection.due}` : 'today'}`}
              </p>
              {/* Under 15 minutes away, in a browser rather than the app: say the phone may miss it.
                  The instant is the save's own (a chip's clock, else the 9:00 default above). */}
              <PhoneMayMissNote
                reminderAt={localInstant(
                  remindAt?.date ?? (detection.due as ISODate | null) ?? clock?.date ?? localToday(),
                  remindAt?.hhmm ?? '09:00',
                )}
              />
            </div>
          )}

          {saved && (
            <p role="status" className="text-[11.5px] leading-[1.45] text-ink-3">
              Saved to <strong className="font-semibold text-ink">{saved.destination}</strong>.
            </p>
          )}

          {capture.error && (
            <p role="status" className="text-[11.5px] leading-[1.45] text-danger text-pretty">
              {capture.error instanceof Error
                ? capture.error.message
                : 'That could not be saved, and the reason did not come back with a sentence attached.'}
            </p>
          )}
        </div>

        {/* 4 · THE FOOTER. */}
        <div
          className="mt-4 flex flex-none flex-col gap-[10px] border-t border-fill bg-surface-2 px-[18px] pb-4 pt-[14px]"
          style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 24px))' }}
        >
          <div className="flex gap-[10px]">
            <button
              type="button"
              disabled={!hasContent(draft) || capture.isPending}
              onClick={() => save(false)}
              className="flex h-[50px] flex-1 items-center justify-center rounded-preview bg-ink text-[15px] font-semibold leading-none text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)] disabled:opacity-40"
            >
              {capture.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              aria-label="Save and open"
              disabled={!hasContent(draft) || capture.isPending}
              onClick={() => save(true)}
              className="flex size-[50px] flex-none items-center justify-center rounded-preview border border-line-2 bg-surface text-ink-2 active:bg-fill disabled:opacity-40"
            >
              <MArrowRight />
            </button>
          </div>
          <p className="text-center text-[11px] font-normal leading-[1.4] text-microcopy">
            no required fields · nothing else to fill in · swipe down to close
          </p>
          {/* Demo mode saves for real (into the in-memory dataset); a real build
              writes through /api/capture/quick. Neither needs a caveat here —
              `data.isDemo` is already stated on every screen's own band. */}
          {data.isDemo && (
            <p className="text-center text-[10.5px] leading-[1.4] text-microcopy">
              Demo data — this saves into the in-memory dataset.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * The chip order. The mockup puts the DETECTED type first ("Clip · Task · Note ·
 * Reminder" on a link) and the README's own list is "Task Note Clip Reminder" —
 * the rendered mockup wins on layout, and the layout it draws is "whatever was
 * detected, then the rest in their fixed order". Leading with the detection is
 * what makes "shown, not asked" true on a 412px row that cannot hold four chips
 * without wrapping.
 */
function ORDERED_TYPES(active: CaptureType | null): CaptureType[] {
  if (active === null) return CAPTURE_TYPES
  return [active, ...CAPTURE_TYPES.filter((t) => t !== active)]
}

/** One preset chip in the sheet — the type chips' shape, a size down. */
function WhenChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx(
        'flex h-[34px] items-center rounded-pill px-[13px] text-[12.5px] leading-none',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        active
          ? 'bg-ink font-semibold text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]'
          : 'bg-fill-2 font-medium text-ink-2 active:bg-fill',
      )}
    >
      {children}
    </button>
  )
}

/** '15:00' → '3:00 PM', for the one sentence under the chips. */
function clockLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number) as [number, number]
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** `day` at `hhmm`, local, as an ISO instant — how `save` builds `reminderAt`. Null if unparseable. */
function localInstant(day: string, hhmm: string): string | null {
  const at = new Date(`${day}T${hhmm}`)
  return Number.isNaN(at.getTime()) ? null : at.toISOString()
}

/** The browser's own local day, the fallback when the clock read has not landed yet. */
function localToday(): ISODate {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` as ISODate
}
